/** @format */

import React, { useEffect, useState, useMemo } from "react";
import Customize_Reworked from "./index"; // Import the layout wrapper
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion"; // Added AnimatePresence
import { Crown, Info, Shield, HelpCircle } from "lucide-react"; // Added Shield, HelpCircle
import { customizeProduct } from "../../actions/blindActions"; // Assuming getValances is not needed here directly if data is in productInfo.valanceArray
import { description, title } from "../../constants/tooltipConstants"; // Assuming these exist
import ImageZoomWithButton from "../../components/button/ImageZoomWithButton";
import { Tooltip } from "./Tooltip"; // Assuming Tooltip component exists
import { Link } from "react-router-dom";

// Helper function to get display name
function getValanceTypeDisplayName(valanceName) {
	if (!valanceName) return "";
	// Keep mapping consistent or simplify if needed
	const nameMap = {
		"zero gravity": "Glide Chainless", // This seems like a control type, might be incorrect mapping here?
		'sb 3" fascia': '3" Fascia',
		"sb compact curved cassette": "Compact Curved Cassette",
		'sb 4" fascia': '4" Fascia',
		"sb curved cassette": "Curved Cassette",
		// Add any other specific mappings
	};
	return nameMap[valanceName.toLowerCase()] || valanceName; // Return mapped name or original
}

// --- Reusable Option Card Component (Copied from previous step, could be moved to a shared file) ---
const OptionCard = ({
	selected,
	displayCountryPrice, // Pass this down if needed for price display on card
	onClick,
	image,
	title: cardTitle, // Renamed prop to avoid conflict with tooltip title constant
	tooltip, // Expect { title: string, content: string }
	price = 0,
	disabled = false, // Add disabled state
	children,
	showPrice = true,
	radioName = "option-card-radio", // Default name for radio group
	value, // Value for the radio input
}) => (
	<motion.label // Use label for accessibility
		htmlFor={`opt-${value}`} // Link label to radio
		whileHover={
			!disabled ? { y: -3, boxShadow: "0 4px 10px rgba(0,0,0,0.05)" } : {}
		}
		transition={{ duration: 0.15 }}
		className={`relative p-4 border-2 rounded-xl transition-all flex flex-col ${
			disabled
				? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
				: selected
				? "border-primary bg-primary/5 shadow-md cursor-pointer"
				: "border-gray-200 hover:border-gray-300 bg-white cursor-pointer"
		}`}
		// onClick should be handled by the radio input change event if possible,
		// but keeping onClick on label is common for card-like selections.
		// Ensure clicking anywhere triggers the radio selection.
		onClick={!disabled ? onClick : undefined}
	>
		{/* Image */}
		<div
			className={`rounded-lg mb-3 overflow-hidden border border-gray-100 ${
				disabled ? "grayscale" : "bg-gray-50"
			}`}
		>
			<ImageZoomWithButton
				item={{ image: image, colour: cardTitle }}
				isFabricSelected={selected}
				customize={true}
				hideButton={true}
			/>
		</div>

		{/* Content */}
		<div className="space-y-3 mt-auto pt-2">
			{/* Title, Radio, Tooltip */}
			<div className="flex items-start justify-between gap-2">
				<div className="flex items-center gap-2 flex-grow">
					<input
						id={`opt-${value}`}
						aria-label={cardTitle}
						type="radio"
						name={radioName} // Group related options
						value={value}
						checked={selected}
						onChange={onClick} // Let radio change trigger the handler
						disabled={disabled}
						className="w-4 h-4 mt-0.5 text-primary focus:ring-primary/50 focus:ring-offset-1 flex-shrink-0"
					/>
					<span className="font-medium text-sm leading-snug">{cardTitle}</span>
				</div>
				{tooltip && tooltip.content && (
					<Tooltip title={tooltip.title || cardTitle} content={tooltip.content}>
						<button
							type="button"
							className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100 flex-shrink-0"
						>
							<Info size={12} />
						</button>
					</Tooltip>
				)}
			</div>

			{/* Price Display */}
			{showPrice && (
				<p
					className={`font-medium text-sm ${
						price > 0 ? "text-primary" : "text-green-600"
					}`}
				>
					{/* Add check for disabled state if price shouldn't show */}
					{disabled
						? "-"
						: price === 0
						? "Included"
						: `+${displayCountryPrice(price)}`}
				</p>
			)}

			{/* Child elements (like color/roll type selects) */}
			{children}
		</div>
	</motion.label>
);

// --- Main Valance Step Component ---
const Customize_Valance = () => {
	/*===============================  VARIABLES  =================================*/
	const dispatch = useDispatch();
	const { productInfo = {}, optionInfo = {} } = useSelector(
		(state) => state.customize?.customizeProduct || {}
	);
	const country = useSelector((state) => state.country);

	// --- Component State ---
	const [loading, setLoading] = useState(false); // Loading state primarily for initial checks/data transformation
	const [error, setError] = useState(null);

	// Selections (Initialize from Redux store)
	const [valance, setValance] = useState(productInfo?.Valance || "");
	const [valanceColor, setValanceColor] = useState(
		productInfo?.Valance_Color || "-"
	);
	const [lChannel, setLChannel] = useState(productInfo?.L_Channel || "None"); // Default to 'None'
	const [selectedRollType, setSelectedRollType] = useState(
		productInfo?.Roll_Type || "Regular"
	); // Default to 'Regular'

	// Data derived from Redux store
	// Ensure valanceArray exists and is an array before using it
	const availableValances = useMemo(
		() =>
			Array.isArray(productInfo?.valanceArray) ? productInfo.valanceArray : [],
		[productInfo?.valanceArray]
	);

	// Prices for L-Channels (assuming these are relatively static or fetched earlier)
	// It might be better if these prices came within the valanceArray or price data
	const channelPrices = useMemo(() => {
		const lChannelPrice = productInfo?.channelPrice?.[0] || 0; // Placeholder structure
		const lbChannelPrice = productInfo?.channelPrice?.[1] || 0;
		return {
			None: 0,
			"L Channels": lChannelPrice,
			"L Channels & Bottom Channels": lbChannelPrice,
		};
	}, [productInfo?.channelPrice]); // Adjust dependency if price source changes

	/*===============================  USE EFFECT  ================================*/

	// Validate initial selections when component mounts or availableValances change
	useEffect(() => {
		setLoading(true);
		setError(null);

		if (availableValances.length === 0) {
			// If control step didn't provide valance options, show error or guide user back
			console.warn("No available valances passed from previous step.");
			setError(
				"Valance options not determined. Please ensure a compatible Control System was selected."
			);
			setLoading(false);
			return;
		}

		// Check if the current Redux `valance` is actually in the available options
		const currentValanceIsValid = availableValances.some(
			(item) => item.valance === valance
		);

		let correctedValance = valance;
		let correctedColor = valanceColor;
		let correctedRoll = selectedRollType;
		let correctedLChannel = lChannel; // L-Channel correction might depend on valance

		if (!currentValanceIsValid || !valance) {
			// Default to the first available valance if current is invalid or not set
			correctedValance = availableValances[0].valance;
			console.log("Correcting Valance selection to:", correctedValance);
		}

		// Find data for the (potentially corrected) valance
		const selectedValanceData = availableValances.find(
			(item) => item.valance === correctedValance
		);

		if (selectedValanceData) {
			// Check color validity
			const availableColors = selectedValanceData.color || [];
			if (correctedValance === "Open Roll") {
				correctedColor = "-"; // Force no color for Open Roll
			} else if (
				!availableColors.includes(correctedColor) ||
				correctedColor === "-"
			) {
				correctedColor = availableColors[0] || "-"; // Default to first color or '-'
				console.log("Correcting Valance Color to:", correctedColor);
			}

			// Check Roll Type validity (Only relevant if roll_supported is true)
			if (!selectedValanceData.roll_supported && correctedRoll !== "Regular") {
				correctedRoll = "Regular"; // Force regular if reverse isn't supported
				console.log(
					"Correcting Roll Type to 'Regular' as selected valance doesn't support Reverse."
				);
			}

			// Check L-Channel validity (Example: Maybe disable L-Channels for Open Roll?)
			// This logic needs clarification based on product rules
			// if (correctedValance === "Open Roll" && correctedLChannel !== 'None') {
			//      correctedLChannel = 'None';
			//      console.log("Correcting L-Channel to 'None' for Open Roll.");
			// }
		} else {
			// Handle case where even the default valance wasn't found (data issue)
			console.error(
				"Could not find data for default/corrected valance:",
				correctedValance
			);
			setError("Internal error: Could not process valance options.");
			correctedColor = "-";
			correctedRoll = "Regular";
			correctedLChannel = "None";
		}

		// Update state only if corrections were made
		if (correctedValance !== valance) setValance(correctedValance);
		if (correctedColor !== valanceColor) setValanceColor(correctedColor);
		if (correctedRoll !== selectedRollType) setSelectedRollType(correctedRoll);
		if (correctedLChannel !== lChannel) setLChannel(correctedLChannel);

		// If no corrections were needed, ensure loading is still set to false
		setLoading(false);
	}, [availableValances]); // Rerun validation if available valances change

	// Dispatch updates whenever a relevant state variable changes
	useEffect(() => {
		if (loading || error) return; // Don't dispatch while loading or if error
		dispatchDetails();
	}, [valance, valanceColor, lChannel, selectedRollType, loading, error]); // Include all relevant state

	/*===============================  FUNCTIONS  =================================*/

	// Dispatch current selections to Redux
	const dispatchDetails = () => {
		if (!productInfo) return; // Guard clause
		dispatch(
			customizeProduct({
				productInfo: {
					...productInfo,
					Valance: valance,
					Valance_Color: valanceColor,
					L_Channel: lChannel,
					Roll_Type: selectedRollType,
					// Program: `Customized ${productInfo.Type} Shade`, // Already set?
					// series: productInfo.SG_Color, // Is 'series' meant to be color? Clarify usage
					error: !!error, // Update error status
				},
				optionInfo: { ...optionInfo },
			})
		);
	};

	// Handle selecting a different valance type
	const handleValanceSelect = (selectedValanceType) => {
		if (selectedValanceType === valance) return; // No change

		setValance(selectedValanceType);

		// Find data for the new selection
		const selectedData = availableValances.find(
			(item) => item.valance === selectedValanceType
		);
		if (!selectedData) return;

		// Reset color and roll type based on the new selection
		if (selectedValanceType === "Open Roll") {
			setValanceColor("-");
			// Reset Roll Type only if Reverse isn't supported (assuming Open Roll supports Regular)
			if (!selectedData.roll_supported && selectedRollType !== "Regular") {
				setSelectedRollType("Regular");
			}
		} else {
			const availableColors = selectedData.color || [];
			// If current color is invalid for new valance, default to first color
			if (!availableColors.includes(valanceColor)) {
				setValanceColor(availableColors[0] || "-");
			}
			// Reset Roll Type if Reverse isn't supported by the new valance
			if (!selectedData.roll_supported && selectedRollType !== "Regular") {
				setSelectedRollType("Regular");
			}
		}
		// Reset L-Channel if necessary based on rules (e.g., disable for Open Roll)
		// if (selectedValanceType === "Open Roll" && lChannel !== 'None') {
		//     setLChannel('None');
		// }
	};

	// Check if a given valance type is currently selected
	const isSelected = (itemValanceType) => {
		if (!itemValanceType || !valance) return false;
		return itemValanceType.toLowerCase() === valance.toLowerCase();
	};

	// --- Display Price Helper ---
	const displayCountryPrice = (price) => {
		const numPrice = parseFloat(price) || 0;
		const discount1 = parseFloat(productInfo?.Discount) || 0;
		const discount2 = parseFloat(productInfo?.Global_Discount) || 0;
		const factor = 1.5; // Markup/Conversion
		const exchangeRate = country === "USA" ? 0.75 : 1;
		const currencyCode = country === "USA" ? "USD" : "CAD";

		const finalPrice =
			numPrice * factor * (1 - discount1) * (1 - discount2) * exchangeRate;
		return `$${finalPrice.toFixed(2)} ${currencyCode}`;
	};

	/*===============================  RENDER  ====================================*/
	return (
		// Wrap step content in the layout component
		<Customize_Reworked>
			<div className="space-y-8">
				{/* --- Header --- */}
				<div className="flex items-center justify-between pb-2 border-b border-gray-200">
					<div className="flex items-center gap-2">
						<div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
							<Crown className="w-5 h-5 text-primary" />
						</div>
						<h3 className="text-lg font-medium">
							Select Valance / Cassette Style
						</h3>
					</div>
					{/* Optional Help Link */}
					<Link
						to="/guides/measuring-guide"
						className="text-primary text-xs hover:underline ml-auto inline-flex items-center gap-1"
						target="_blank"
						rel="noopener noreferrer"
					>
						<HelpCircle size={14} /> Valance Help
					</Link>
				</div>

				{/* --- Conditional Rendering: Loading, Error, Options --- */}
				<AnimatePresence mode="wait">
					{loading ? (
						<motion.div
							/* Loading indicator */ key="loading"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="flex flex-col items-center justify-center h-40 text-gray-500"
						>
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3"></div>
							<p>Loading valance options...</p>
						</motion.div>
					) : error ? (
						<motion.div
							/* Error message */ key="error"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="flex flex-col items-center justify-center h-40 text-red-600 bg-red-50 p-6 rounded-lg border border-red-200"
						>
							<p className="font-medium text-center">{error}</p>
							<Link
								to="/customize/control"
								className="mt-4 text-sm text-primary hover:underline"
							>
								Go back to Control System
							</Link>
						</motion.div>
					) : (
						// --- Valance Options Grid ---
						<motion.fieldset // Use fieldset for grouping
							key="options"
							initial="initial"
							animate="animate"
							variants={{
								initial: { opacity: 0 },
								animate: { opacity: 1, transition: { staggerChildren: 0.05 } },
							}}
							className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
						>
							<legend className="sr-only">Valance Options</legend>{" "}
							{/* Hidden legend */}
							{availableValances.map((item) => {
								const currentSelection = isSelected(item.valance);
								const itemDisplayName = getValanceTypeDisplayName(item.valance);
								const tooltipTitle = title[item.valance] || itemDisplayName;
								const tooltipDescription =
									description[item.valance] ||
									`Details for ${itemDisplayName}.`;
								const itemPrice = parseFloat(item.price) || 0;

								return (
									<motion.div // Using div for layout, OptionCard handles label/input
										key={item.valance}
										variants={{
											initial: { opacity: 0, y: 10 },
											animate: { opacity: 1, y: 0 },
										}}
									>
										<OptionCard
											displayCountryPrice={displayCountryPrice}
											selected={currentSelection}
											onClick={() => handleValanceSelect(item.valance)}
											image={item.img}
											title={itemDisplayName} // Pass display name to OptionCard
											tooltip={{
												title: tooltipTitle,
												content: tooltipDescription,
											}}
											price={itemPrice}
											radioName="valance-option" // Group radios
											value={item.valance} // Value for the radio input
										>
											{/* Nested selects appear as children of OptionCard */}
											<div className="space-y-3 pt-3 border-t border-gray-200/60">
												{/* Valance Color (Conditional) */}
												{item.valance !== "Open Roll" && (
													<div className="space-y-1.5">
														<label
															htmlFor={`val-color-${item.valance}`}
															className="block text-xs font-medium text-gray-600"
														>
															Valance Colour
														</label>
														<select
															id={`val-color-${item.valance}`}
															aria-label={`${itemDisplayName} Colour`}
															value={currentSelection ? valanceColor : ""}
															onChange={(e) => setValanceColor(e.target.value)}
															disabled={!currentSelection}
															onClick={(e) => e.stopPropagation()}
															className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-0 focus:border-primary outline-none appearance-none transition-opacity duration-150 ${
																!currentSelection
																	? "opacity-50 cursor-not-allowed"
																	: ""
															}`}
														>
															{/* <option value="" disabled={currentSelection}>--Select--</option> */}
															{(item.color || []).map((colour) => (
																<option key={colour} value={colour}>
																	{colour}
																</option>
															))}
															{(item.color || []).length === 0 && (
																<option value="-">-</option>
															)}
														</select>
													</div>
												)}

												{/* Roll Type (Conditional) */}
												{item.roll_supported && (
													<div className="space-y-1.5">
														<label
															htmlFor={`roll-type-${item.valance}`}
															className="block text-xs font-medium text-gray-600"
														>
															Roll Type
														</label>
														<select
															id={`roll-type-${item.valance}`}
															aria-label="Roll Type"
															value={
																currentSelection ? selectedRollType : "Regular"
															}
															onChange={(e) =>
																setSelectedRollType(e.target.value)
															}
															disabled={!currentSelection}
															onClick={(e) => e.stopPropagation()}
															className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-0 focus:border-primary outline-none appearance-none transition-opacity duration-150 ${
																!currentSelection
																	? "opacity-50 cursor-not-allowed"
																	: ""
															}`}
														>
															<option value="Regular">
																Regular Roll (Fabric off back)
															</option>
															<option value="Reverse">
																Reverse Roll (Fabric off front)
															</option>
														</select>
													</div>
												)}
											</div>
										</OptionCard>
									</motion.div>
								);
							})}
						</motion.fieldset>
					)}
				</AnimatePresence>

				{/* --- Light Guards (Side Channels) Section --- */}
				{/* Conditionally render based on product type (e.g., only for Roller?) */}
				{/* {productInfo?.Type === "Roller" &&
					!loading &&
					!error && ( // Example condition
						<fieldset className="space-y-4 pt-6 border-t border-gray-200">
							<legend className="text-lg font-medium mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
								<div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
									<Shield className="w-5 h-5 text-primary" />
								</div>
								Optional: Light Guards (Side & Bottom Channels)
							</legend>
							<p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
								Add side channels (Light Guards) and an optional bottom channel
								to minimize light gaps, ideal for blackout fabrics in bedrooms
								or media rooms. Ensure your window frame has adequate mounting
								depth.{" "}
								<Link
									to="/guides/light-guards"
									className="text-primary hover:underline font-medium"
									target="_blank"
									rel="noopener noreferrer"
								>
									Learn more
								</Link>
							</p>

							<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
								<OptionCard
									displayCountryPrice={displayCountryPrice}
									selected={lChannel === "None"}
									onClick={() => setLChannel("None")}
									image="/images/diagram/lc/none.png" // Use appropriate placeholder images
									title="None"
									value="None" // Value for radio input
									radioName="l-channel-option"
									showPrice={false} // No price for 'None'
								/>
								<OptionCard
									displayCountryPrice={displayCountryPrice}
									selected={lChannel === "L Channels"}
									onClick={() => setLChannel("L Channels")}
									image="/images/diagram/lc/l_side_channels.png"
									title="Side Light Guards"
									value="L Channels"
									radioName="l-channel-option"
									tooltip={{
										title: title["L Channels"],
										content: description["L Channels"],
									}}
									price={channelPrices["L Channels"]} // Use fetched price
								/>
								<OptionCard
									displayCountryPrice={displayCountryPrice}
									selected={lChannel === "L Channels & Bottom Channels"}
									onClick={() => setLChannel("L Channels & Bottom Channels")}
									image="/images/diagram/lc/l_side_bottom_channels.png"
									title="Side & Bottom Light Guards"
									value="L Channels & Bottom Channels"
									radioName="l-channel-option"
									tooltip={{
										title: title["L Channels & Bottom Channels"],
										content: description["L Channels & Bottom Channels"],
									}}
									price={channelPrices["L Channels & Bottom Channels"]} // Use fetched price
								/>
							</div>
						</fieldset>
					)} */}
			</div>
		</Customize_Reworked>
	);
};

export default Customize_Valance;
